import type { UtmInputType } from "@/types/graphql/site.generated";

/**
 * SSE-клиент помощника зрителя (спека 2026-09-23-viewer-assistant, §5).
 * EventSource не умеет POST, поэтому поток читается через fetch и
 * ReadableStream. Бэкенд пишет каждое событие блоком
 * "event: <type>\ndata: <json>\n\n"; куски сети могут резать блок где угодно.
 */
export type AssistantBlockedReason =
  | "DISABLED"
  | "BUDGET"
  | "DAILY_LIMIT"
  | "OFF_TOPIC_BLOCK";

export interface AssistantRequestDraft {
  id: string;
  type: string;
  message: string;
  paymentId: string | null;
}

export type AssistantStreamEvent =
  | { type: "conversation"; conversationId: string }
  | { type: "delta"; text: string }
  | { type: "request_draft"; request: AssistantRequestDraft }
  | { type: "off_topic" }
  | { type: "blocked"; reason: AssistantBlockedReason }
  | { type: "error" }
  | { type: "done"; messageId: string };

export interface AssistantMessageRequest {
  conversationId: string | null;
  text: string;
  locale: string;
  pageUrl: string;
  contentId: string | null;
  /** Продукт страницы /product/<id>: помощник открывает его GetProduct. */
  productId: string | null;
  origin: "BUBBLE" | "ERROR";
  errorCode: string | null;
  paymentId: string | null;
  /** Детали ошибки плеера (тип и детали hls.js, HTTP-код) для PLAYBACK_ERROR,
   * до PLAYER_ERROR_MAX_LENGTH символов. */
  playerError: string | null;
  /** Последнее касание браузера (спека attribution 5.7). */
  utm: UtmInputType | null;
}

export const ASSISTANT_MESSAGES_URL = "/api/assistant/messages";

/** Целые блоки превращает в события, недописанный хвост возвращает в rest. */
export function parseSseChunk(buffer: string): {
  events: AssistantStreamEvent[];
  rest: string;
} {
  const events: AssistantStreamEvent[] = [];
  const parts = buffer.split("\n\n");
  const rest = parts.pop() ?? "";
  for (const part of parts) {
    const data = part.split("\n").find((line) => line.startsWith("data: "));
    if (!data) continue;
    try {
      events.push(JSON.parse(data.slice(6)) as AssistantStreamEvent);
    } catch {
      // битый кусок пропускаем
    }
  }
  return { events, rest };
}

export async function streamAssistantMessage(
  body: AssistantMessageRequest,
  onEvent: (e: AssistantStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  // Токен из localStorage, как у Apollo; httpOnly-кука auth уходит сама.
  const token =
    typeof window !== "undefined"
      ? window.localStorage.getItem("auth-token")
      : null;
  const res = await fetch(ASSISTANT_MESSAGES_URL, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });
  if (res.status === 401) throw new Error("UNAUTHORIZED");
  if (!res.ok || !res.body) {
    onEvent({ type: "error" });
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = parseSseChunk(buffer);
    buffer = rest;
    events.forEach(onEvent);
  }
}
